import type { CdekTrackingData } from './api/schemas/cdek.schema'
import { CdekShipmentDirection } from './cdek-domain'
import {
  cdekStatusLabelForCode,
  firstAcceptedCdekStatus,
  latestCdekStatus,
} from './cdek-status-history'
import {
  CDEK_STATUS_VOCABULARY,
  classifyCdekStatus,
  type CdekHandoffFact,
  type CdekShipmentState,
} from './cdek-status-vocabulary.generated'

export type CustomerDeliveryStateCode = 'idle' | CdekShipmentState

/** One courier as a stored DELIVERY COURIER_INFO event carried it; absent values stay null. */
export interface CustomerCourier {
  name: string | null
  phone: string | null
  car_model: string | null
  registration_mark: string | null
  assigned_at: string
}

export type CustomerCouriers =
  | { state: 'assigned'; current: CustomerCourier; past: CustomerCourier[] }
  | { state: 'not_received'; current: null; past: [] }

export interface CustomerShipmentState {
  cdekNumber: string
  orderId?: number
  customerId?: string
  direction?: CdekShipmentDirection
  state: CustomerDeliveryStateCode
  statusCode?: string
  statusName?: string
  changedAt: string
  plannedDeliveryDate?: string
  deliveryDate?: string
  courier?: CdekTrackingData['courier']
  /** Server-served history from stored COURIER_INFO events; the customer card reads only this. */
  couriers?: CustomerCouriers
  problemCode?: string
  agreement?: {
    date?: string
    timeFrom?: string
    timeTo?: string
    type?: string
    comment?: string
  }
  /** Monotonic backend/domain proof for this shipment leg only. */
  handoffAccepted?: boolean
  /** The fold's own anchors, as served; never re-derived from array order. */
  firstAcceptedAt?: string
  latestObservedAt?: string
  /** The monotonic handoff fact the server decided for this leg. */
  handoff?: CdekHandoffFact
  /** The winning observation carried an unreviewed provider code. */
  needsReview?: boolean
}

export interface CustomerStateTransition {
  id: string
  customerId: string
  from: CustomerDeliveryStateCode
  to: CustomerDeliveryStateCode
  occurredAt: string
  source: 'cdek_order' | 'cdek_webhook'
  sourceEvent?: string
  cdekNumber?: string
  orderId?: number
}

export interface CustomerDeliveryState {
  customerId: string
  state: CustomerDeliveryStateCode
  changedAt: string
  hasActiveDelivery: boolean
  activeShipmentCount: number
  shipments: CustomerShipmentState[]
  recentTransitions: CustomerStateTransition[]
}

export interface CdekHandoffEvidence {
  direction?: CdekShipmentDirection
  shipmentState?: CustomerDeliveryStateCode
  currentStatusCode?: string
  statusHistory?: ReadonlyArray<{ code?: string }>
}

/**
 * The closed states that unambiguously follow the sender handoff.
 *
 * Derived from the generated vocabulary rather than restated: a state no
 * `not_yet` code can reach is a state the parcel can only be in once it has
 * left the sender. A second literal here would drift from the server's own.
 */
const PROGRESSED_STATES: ReadonlySet<CustomerDeliveryStateCode> = new Set(
  Object.values(CDEK_STATUS_VOCABULARY)
    .filter((facts) => facts.handoff !== 'not_yet')
    .map((facts) => facts.stage)
    .filter((stage) => !Object.values(CDEK_STATUS_VOCABULARY)
      .some((facts) => facts.handoff === 'not_yet' && facts.stage === stage)),
)

export function isAcceptedByCdek(statusCode?: string): boolean {
  return classifyCdekStatus(statusCode)?.handoff === 'accepted_at_sender'
}

/** Monotonic sender-handoff fact for exactly one outbound shipment leg. */
export function hasCdekSenderHandoff(evidence: CdekHandoffEvidence): boolean {
  if (evidence.direction !== CdekShipmentDirection.Outbound) return false
  const moved = (statusCode?: string) =>
    (classifyCdekStatus(statusCode)?.handoff ?? 'not_yet') !== 'not_yet'
  return moved(evidence.currentStatusCode)
    || Boolean(evidence.statusHistory?.some(status => moved(status.code)))
    || Boolean(evidence.shipmentState && PROGRESSED_STATES.has(evidence.shipmentState))
}

/**
 * The Russian label for one provider code, optionally located.
 *
 * The provider's own display string is deliberately not an input: it was a
 * second source of customer-visible copy, and the generated vocabulary is now
 * the only client-side one. Prefer the server's `statusName` wherever a
 * projection supplies it; this exists for the one provider-shaped history view
 * `docs/client-call-site-inventory.md` records.
 */
export function formatCdekStatusName(statusCode?: string, city?: string): string {
  return cdekStatusLabelForCode(statusCode, city)
}

export const ACTIVE_CUSTOMER_STATES = new Set<CustomerDeliveryStateCode>([
  'preparing',
  'in_transit',
  'ready_for_pickup',
  'out_for_delivery',
  'delivery_issue',
])

export function isCurrentCustomerOrder(
  orderStatus: string,
  shipmentState?: CustomerDeliveryStateCode
): boolean {
  if (shipmentState && ACTIVE_CUSTOMER_STATES.has(shipmentState)) return true
  return !['DELIVERED', 'CANCELLED', 'UNPAID'].includes(orderStatus)
}

/**
 * Read a stored or historical code through the generated map.
 *
 * An unreviewed code keeps the caller's fallback rather than borrowing a
 * neighbouring stage. A projection served by the API already carries its
 * closed state; this is the read path for rows written before it did.
 */
export function stateFromCdekStatus(
  statusCode?: string,
  fallback: CustomerDeliveryStateCode = 'preparing'
): CustomerDeliveryStateCode {
  return classifyCdekStatus(statusCode)?.stage ?? fallback
}

export function shipmentFromCdekOrder(
  data: CdekTrackingData,
  orderId?: number,
  customerId?: string,
  previous?: CustomerShipmentState
): CustomerShipmentState | undefined {
  if (!data.cdek_number) return undefined
  const latest = latestCdekStatus(data.statuses)
  const accepted = firstAcceptedCdekStatus(data.statuses)
  const changedAt = latest?.date_time || data.delivery_date || new Date().toISOString()
  const direction = data.is_return || data.is_reverse
    ? CdekShipmentDirection.Return
    : (previous?.direction || CdekShipmentDirection.Outbound)
  const statusCode = latest?.code || previous?.statusCode
  const state = stateFromCdekStatus(statusCode, previous?.state || 'preparing')
  return {
    ...previous,
    cdekNumber: data.cdek_number,
    orderId: orderId ?? previous?.orderId,
    customerId: customerId || previous?.customerId,
    direction,
    state,
    statusCode,
    statusName: formatCdekStatusName(statusCode, latest?.city),
    changedAt,
    plannedDeliveryDate: data.planned_delivery_date || previous?.plannedDeliveryDate,
    deliveryDate: data.delivery_date || previous?.deliveryDate,
    handoffAccepted: hasCdekSenderHandoff({
      direction,
      shipmentState: state,
      currentStatusCode: statusCode,
      statusHistory: data.statuses,
    }),
    firstAcceptedAt: accepted?.date_time || previous?.firstAcceptedAt,
    latestObservedAt: latest?.date_time || previous?.latestObservedAt,
  }
}

const STATE_PRIORITY: Record<CustomerDeliveryStateCode, number> = {
  delivery_issue: 90,
  out_for_delivery: 80,
  ready_for_pickup: 70,
  in_transit: 60,
  preparing: 50,
  delivered: 30,
  returned: 20,
  cancelled: 10,
  idle: 0,
}

export function resolveCustomerState(
  shipments: CustomerShipmentState[]
): CustomerDeliveryStateCode {
  if (shipments.length === 0) return 'idle'
  return [...shipments].sort((left, right) => {
    const priority = STATE_PRIORITY[right.state] - STATE_PRIORITY[left.state]
    if (priority) return priority
    return Date.parse(right.changedAt) - Date.parse(left.changedAt)
  })[0].state
}
